import { Ref, ref } from 'vue';
import { createPendingPromiseManager } from '@/store/pendingPromiseManager';

export function createResourceLoadingMixin() {
  const pendingPromiseManager = createPendingPromiseManager();
  const resources: Ref<Map<string, any>> = ref(new Map());

  async function loadResource<T>(key: string, asyncFunc: () => Promise<T>, forceReload = false): Promise<T> {
    /**
     * If the resource is already loaded and we are not forcing a reload, return it.
     * Unless forceReload is true or if the resource is currently loading,
     * in which case we should wait for it to finish loading.
     */
    if (resources.value.has(key) && !forceReload && !pendingPromiseManager.isLoadingResource(key)) {
      return resources.value.get(key);
    }
    return pendingPromiseManager.load(key, asyncFunc, forceReload).then((resource) => {
      resources.value.set(key, resource);
      return resource;
    });
  }

  function getResource<T>(key: string): T | undefined {
    return resources.value.get(key);
  }

  function isLoadingResource(key: string): boolean {
    return pendingPromiseManager.isLoadingResource(key);
  }

  function removeResource(key: string) {
    resources.value.delete(key);
  }

  /**
   * Removes the resource with the given key, or all resources with a key starting with the given key.
   * Useful for paginated resources which have pagination as part of the key.
   * @param key base key to remove
   */
  function removeResourcePaginated(key: string) {
    // remove resources with a key starting with the key
    const keys = Array.from(resources.value.keys());
    for (const k of keys) {
      if (k.startsWith(key)) {
        resources.value.delete(k);
      }
    }
  }

  function reset() {
    resources.value.clear();
    pendingPromiseManager.reset();
  }

  return {
    resources,
    loadResource,
    getResource,
    isLoadingResource,
    removeResource,
    removeResourcePaginated,
    reset
  };
}
